import ToastComponent from "./toast-component";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function ModalEmail({ formData, setFormData, isOpen, onAccept, onCancel }) {
    const { t } = useTranslation();

    const charLimits = {
        codigo: 64,
        descripcion: 128,
        nota: 512,
    };

    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("warning");
    const descripcionInputRef = useRef(null);

    const showToast = (message, type = "warning", durationMs = 3000) => {
        setToastMessage(message);
        setToastType(type);
        setToastVisible(true);
        window.clearTimeout(showToast._timer);
        showToast._timer = window.setTimeout(() => {
            setToastVisible(false);
        }, durationMs);
    };

    // Focus descripcion input when modal opens and descripcion is not empty
    useEffect(() => {
        if (isOpen && descripcionInputRef.current) {
            const descripcionValue = String(formData.descripcion || "").trim();
            if (descripcionValue.length > 0) {
                // Use setTimeout to ensure the input is rendered and focusable
                setTimeout(() => {
                    if (descripcionInputRef.current) {
                        descripcionInputRef.current.focus();
                        // Set cursor position to the end of the string
                        const length = descripcionValue.length;
                        descripcionInputRef.current.setSelectionRange(length, length);
                    }
                }, 0);
            }
        }
    }, [isOpen, formData.descripcion]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (value.length <= charLimits[name]) {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    };

    const validateForm = () => {
        try {
            let message = "";
            if (String(formData.descripcion || "").trim().length === 0) {
                message = message + t("alert_debe_ingresar_descripcion") + '\n';
            }
            if (message.length > 0) {
                showToast(message, "warning", 3500);
                return;
            }
            onAccept(formData);
        } catch (err) {
            console.log('validateForm err');
            console.log(err);
        }
    };

    const cancelForm = () => {
        setFormData({
            ...formData,
            codigo: "",
            descripcion: "",
            nota: "",
        });
        onCancel();
    };

    return (
        <>
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
                    onClick={cancelForm}
                >
                    <div
                        className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="text-xl font-bold mb-4 text-gray-700">{t("label_gestionar_email")}</h2>

                        {/* Inputs */}
                        <div className="mb-4">
                            <label className="block text-gray-600 font-semibold mb-1">
                                {t("label_codigo")}
                            </label>
                            <input
                                name="codigo"
                                value={formData.codigo || ""}
                                onChange={handleInputChange}
                                placeholder={t("placeholder_ingresa_codigo")}
                                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                                maxLength={charLimits.codigo}
                            />
                            <p className="text-sm text-gray-500 flex justify-end">{(formData.codigo || "").length}/{charLimits.codigo}</p>
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-600 font-semibold mb-1">
                                {t("label_descripcion")}<span className="text-red-500">*</span>
                            </label>
                            <input
                                ref={descripcionInputRef}
                                name="descripcion"
                                value={formData.descripcion || ""}
                                onChange={handleInputChange}
                                placeholder={t("placeholder_ingresa_descripcion")}
                                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                                maxLength={charLimits.descripcion}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        validateForm();
                                    }
                                }}
                            />
                            <p className="text-sm text-gray-500 flex justify-end">{(formData.descripcion || "").length}/{charLimits.descripcion}</p>
                        </div>
                        <div className="mb-4">
                            <label className="block text-gray-600 font-semibold mb-1">{t("label_nota")}</label>
                            <textarea
                                name="nota"
                                value={formData.nota || ""}
                                onChange={handleInputChange}
                                placeholder={t("placeholder_ingresa_nota")}
                                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                                rows={4}
                                maxLength={charLimits.nota}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        validateForm();
                                    }
                                }}
                            />
                            <p className="text-sm text-gray-500 flex justify-end">{(formData.nota || "").length}/{charLimits.nota}</p>
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end gap-4">
                            <button
                                onClick={cancelForm}
                                className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500 transition duration-300"
                            >
                                {t("label_cancelar")}
                            </button>
                            <button
                                onClick={validateForm}
                                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition duration-300"
                            >
                                {t("label_aceptar")}
                            </button>
                        </div>
                    </div>
                    <ToastComponent
                        visible={toastVisible}
                        message={toastMessage}
                        type={toastType}
                        onClose={() => setToastVisible(false)}
                    />
                </div>
            )}
        </>
    );
}

